import { resolveApiUrl } from "@/api/client";
import { htmlAssetUrls, mapHtmlAssetUrls } from "./html-upload-assets";

/** Text edits must never serialize newly rendered local pixels to the server. */
export function canonicalizeHtmlAssetEdit(
  original: string,
  edited: string,
  canonicalize: (url: string) => string,
): string {
  const inlineKey = (url: string) => {
    try {
      const parsed = new URL(url, window.location.href);
      return parsed.protocol === "data:" || parsed.protocol === "blob:"
        ? parsed.href
        : undefined;
    } catch {
      return undefined;
    }
  };
  const authoredInline = new Set(
    htmlAssetUrls(original).map(inlineKey).filter(Boolean),
  );
  return mapHtmlAssetUrls(edited, (url) => {
    const result = canonicalize(url);
    const key = inlineKey(result);
    if (key && !authoredInline.has(key))
      throw new Error(
        "Add images through the attachment control so they stay on this device.",
      );
    return result;
  });
}

/** Private pixels may be displayed only with trusted Denker scripts. */
export function privateHtmlDocument(
  trustedHtml: string,
  authoredHtml: string,
  nonce: string,
): string {
  const origin = new URL(resolveApiUrl("/"), window.location.href).origin;
  const media = `data: blob: ${origin}/api/v1/uploads/ ${origin}/media/uploads/`;
  const policy = `default-src 'none'; script-src 'nonce-${nonce}'; script-src-attr 'none'; style-src 'unsafe-inline'; img-src ${media}; media-src ${media}; connect-src 'none'; frame-src 'none'; object-src 'none'; base-uri 'none'; form-action 'none'`;
  const authored = new DOMParser().parseFromString(authoredHtml, "text/html");
  // These restrictions apply only to the rendered copy, never stored content.
  authored
    .querySelectorAll("script,base,meta[http-equiv]")
    .forEach((node) => node.remove());
  for (const element of authored.querySelectorAll("*")) {
    for (const attribute of [...element.attributes]) {
      if (/^on/i.test(attribute.name) || attribute.name === "nonce")
        element.removeAttribute(attribute.name);
    }
  }
  const trusted = trustedHtml.replace(
    /<script(?=[\s>])/gi,
    `<script nonce="${nonce}"`,
  );
  return `<meta http-equiv="Content-Security-Policy" content="${policy}"><meta name="referrer" content="no-referrer">${trusted}${authored.documentElement.outerHTML}`;
}
